import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { SupportFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { SupportTicketCreationFormContainerProps } from "models/propTypes/supportIssuePropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormFieldConstants } from "utils/constants";
//#endregion
//#region components
import SupportTicketCreationForm from "./creationForm";
import { useSupportService } from "serviceHooks";
import { useCheckRelations } from "components/common";
import { CompanyService } from "services";
import useAgentShadow from "../agent/useAgentShadow";
import { getSupportAttachmentTypes } from "../supportAttachmentTypes";
//#endregion
import * as Sentry from "@sentry/react";
import * as Yup from "yup";

const toPersonItems = (persons: WintUtilsWebApi.SearchResult[]): AutocompleteListItem[] =>
  persons.map((person) => ({ id: person.id, value: person.name ?? "" }));

// Declared once so the conditional `when` branch can be typed as `typeof personsSchema` rather
// than by spelling out this Yup version's five ArraySchema generics.
const personsSchema = Yup.array();

const SupportTicketCreationFormContainer: ForwardRefRenderFunction<
  FormHandles,
  SupportTicketCreationFormContainerProps
> = (props, ref) => {
  const supportService = useSupportService();
  const agentShadow = useAgentShadow();

  const { getIsRevisor } = useCheckRelations();
  const isRevisor = getIsRevisor();

  const supportCategoryOptions = useSelector((store: AppState) => store.common.supportCategoryOptions);
  const agencyMode = supportCategoryOptions.agencyMode;
  const recipientOptions = supportCategoryOptions.recipientOptions ?? [];
  const canSendToCustomer = recipientOptions.includes(WintSuperkollAPIModel.SupportIssueRecipient.Customer);

  const [suggestedPersons, setSuggestedPersons] = React.useState<AutocompleteListItem[]>([]);

  // Only the caller who can address the customer will ever see the person field, so the
  // suggestion request is not fired for anyone else. A failure leaves the field usable via
  // search rather than breaking the form.
  React.useEffect(() => {
    if (!agencyMode || !canSendToCustomer) return;

    supportService
      .getSuggestedSupportPersons()
      .then((persons) => setSuggestedPersons(toPersonItems(persons)))
      .catch(() => setSuggestedPersons([]));
  }, [agencyMode, canSendToCustomer]);

  const searchPersons = (searchText: string) => CompanyService.SearchPersons(searchText).then(toPersonItems);

  const supportTicket = {
    subject: "",
    message: props.defaultMessage ?? "",
    attachments: [],
    persons: [],
  } as SupportFormTypes.Create;

  const validationSchema = Yup.object<SupportFormTypes.Create>({
    category: agencyMode || isRevisor ? Yup.number() : Yup.number().required(TranslationKeys.Validations.Required),
    recipient: agencyMode ? Yup.number().required(TranslationKeys.Validations.Required) : Yup.number(),
    persons: personsSchema.when(
      FormFieldConstants.Support.Create.Recipient,
      ([recipient]: (WintSuperkollAPIModel.SupportIssueRecipient | undefined)[], schema: typeof personsSchema) =>
        (recipient === WintSuperkollAPIModel.SupportIssueRecipient.Customer &&
          schema.required(TranslationKeys.Validations.Required).min(1, TranslationKeys.Validations.Required)) ||
        schema
    ),
    subject: Yup.string().required(TranslationKeys.Validations.Required),
    message: Yup.string().required(TranslationKeys.Validations.Required),
    attachments: Yup.array(),
  });

  const handleFormSubmit = async (values: SupportFormTypes.Create) => {
    const isToCustomer = values.recipient === WintSuperkollAPIModel.SupportIssueRecipient.Customer;

    // In agency mode the server derives the hidden category row from `recipient`, so no
    // categoryId is sent. Do not reintroduce one — the two agency categories are hidden rows
    // the user must never pick, and guessing one silently routes the case to the wrong side.
    const supportIssue: WintSuperkollAPIModel.SupportIssuePost = agencyMode
      ? {
          title: values.subject,
          message: values.message,
          attachments: values.attachments as WintSuperkollAPIModel.SupportAttachment[],
          recipient: values.recipient,
          // SearchResult.id is an optional string, so a malformed item would become NaN and be
          // posted as a null person id. Dropped instead — the schema requires at least one
          // person, so an empty list reaches the server and comes back as its own error rather
          // than creating a case addressed to nobody.
          ...(isToCustomer
            ? {
                personIds: (values.persons ?? [])
                  .map((person) => Number(person.id))
                  .filter((personId) => !Number.isNaN(personId)),
              }
            : {}),
        }
      : {
          categoryId: values.category,
          title: values.subject,
          message: values.message,
          attachments: values.attachments as WintSuperkollAPIModel.SupportAttachment[],
        };

    const result = await supportService.createTicket(supportIssue);

    Sentry.captureMessage("Support team: Support case submitted", {
      level: "info",
      tags: {
        source: "support-dialog",
        hasAttachments: String((values.attachments?.length ?? 0) > 0),
        // The recipient replaces the category in agency mode rather than joining it: there is no
        // category to report, so tagging an empty categoryId would just be noise in Sentry.
        ...(agencyMode
          ? {
              agencyMode: "true",
              recipient:
                values.recipient !== undefined ? WintSuperkollAPIModel.SupportIssueRecipient[values.recipient] : "",
              personCount: String(values.persons?.length ?? 0),
            }
          : { categoryId: String(values.category ?? "") }),
      },
    });

    // Only a question to Wint goes to the agent: in agency mode a case to the agency or to its customer is
    // not ours to answer, and its text is not ours to send on.
    if (!agencyMode || values.recipient === WintSuperkollAPIModel.SupportIssueRecipient.Wint) {
      agentShadow.onCaseSent(result, values.subject, values.message, (values.attachments?.length ?? 0) > 0);
    }
    return result;
  };

  return (
    <SupportTicketCreationForm
      ref={ref}
      validationSchema={validationSchema}
      defaultValues={supportTicket}
      handleSubmit={handleFormSubmit}
      onFormValidationChange={props.onFormValidationChange}
      onFormUpdate={props.onFormUpdate}
      categorySelectionNotAllowed={isRevisor}
      agencyMode={agencyMode}
      agencyName={supportCategoryOptions.agencyName}
      recipientOptions={recipientOptions}
      searchPersons={searchPersons}
      suggestedPersons={suggestedPersons}
      attachmentTypes={getSupportAttachmentTypes(isRevisor)}
    />
  );
};

export default forwardRef(SupportTicketCreationFormContainer);
